import { useState, useEffect, useCallback } from 'react';
import axiosClient from '../../../../../../setup/axios/axiosClient';
import { CreditTransaction } from '../../../models/CreditModel';

interface CreditTransactionResponse {
    items: CreditTransaction[];
    currentPage: number;
    totalPages: number;
    totalItems: number;
    limit: number;
}

type SortOrderType = 'asc' | 'desc'

interface HookParams {
    initialLimit?: number;
    initialSortField?: string;
    initialSortOrder?: SortOrderType;
}

const useCreditTransactions = ({
    initialLimit = 10,
    initialSortField = 'createdAt',
}: HookParams) => {

    const [data, setData] = useState<CreditTransaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(initialLimit);
    const [sortField, setSortField] = useState(initialSortField);
    const [sortOrder, setSortOrder] = useState<SortOrderType>('desc');

    const fetchData = useCallback(async (page: number, currentLimit: number, currentSortField: string) => {

        setLoading(true);
        setError(null);

        const apiUrl = `/usage/monthly-credits-transactions?page=${page}&limit=${currentLimit}&sortField=${currentSortField}&sortOrder=${sortOrder === 'asc' ? 1 : -1}`;

        try {
            const response = await axiosClient.get<CreditTransactionResponse>(apiUrl);

            const result = response.data

            setData(result.items);
            setCurrentPage(result.currentPage);
            setTotalPages(result.totalPages);
            setLimit(result.limit);
        } catch (err) {
            setError('Failed to load credit transactions data.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [sortOrder]);

    // Handlers for UI
    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Handler for sorting
    const handleSort = (field: string) => {
        if (field === sortField) {
            // Toggle order if the same field is clicked
            setSortOrder(order => (order === 'asc' ? 'desc' : 'asc'));
        } else {
            // Set new field, default to descending
            setSortField(field);
            setSortOrder('desc');
        }
        setCurrentPage(1);
    };

    const calculateCreditsAdded = (credit: CreditTransaction) => {
        if (credit.category === 'Free') {
            return credit.amount.toFixed(2);
        }

        const amount = credit.amount;
        const currency = credit.currency;

        if (currency === 'INR') {
            const rate = credit.exchangeRateInINR || 90;
            return (amount / rate).toFixed(2);
        }

        return amount.toFixed(2);
    };


    useEffect(() => {
        fetchData(currentPage, limit, sortField);
    }, [fetchData, currentPage, limit, sortField]);

    useEffect(() => {
        setCurrentPage(1);
    }, [limit]);

    return {
        data,
        loading,
        error,
        currentPage,
        totalPages,
        limit,
        sortField,
        sortOrder,
        goToPage,
        handleSort,
        setLimit,
        calculateCreditsAdded
    };
};

export default useCreditTransactions;